import { formatDateTime } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import { TooltipButton } from "@/v2-backoffice-client/components/button"
import InvitationLogStatus from "@/v2-backoffice-client/invitation/log/InvitationLogStatus"
import { getStatusDescription } from "@/v2-backoffice-client/invitation/log/invitation.log.utils"
import type { InvitationLogFragment } from "@/v2-backoffice-client/invitation/log/__types__/invitation.log.fragment"

export type InvitationViewActivityLogProps = {
  logs: InvitationLogFragment[] | null | undefined
  isRefreshing?: boolean
  onRefresh?: () => void
}

function InvitationViewActivityLogRow(props: {
  log: InvitationLogFragment
  isLatest: boolean
  isFirst: boolean
  isLast: boolean
}) {
  const { log, isLatest, isFirst, isLast } = props
  const description = getStatusDescription(log.StatusCode)

  return (
    <li className="flex gap-3" data-slot="invitation-activity-log-row">
      <div
        className="relative flex w-2 shrink-0 flex-col items-center self-stretch"
        data-slot="invitation-activity-log-rail"
      >
        {!isFirst && (
          <div
            className="pointer-events-none absolute left-1/2 top-0 h-3 w-px -translate-x-1/2 -translate-y-full bg-border"
            aria-hidden
          />
        )}
        <div className="flex h-5 w-full shrink-0 items-center justify-center">
          <div
            className={cn(
              "relative z-[1] box-border size-2 shrink-0 rounded-full border border-solid",
              isLatest
                ? "border-foreground bg-background"
                : "border-border bg-background",
            )}
            aria-hidden
          />
        </div>
        {!isLast && (
          <div className="min-h-4 w-px flex-1 bg-border" aria-hidden />
        )}
      </div>

      <div className="min-w-0 flex-1 pb-4">
        <div className="flex items-center justify-between gap-2">
          <InvitationLogStatus log={log} />
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {formatDateTime(new Date(log.CreatedAt))}
          </span>
        </div>
        {description && (
          <p className="mt-1 text-xs leading-snug text-muted-foreground">
            {description}
          </p>
        )}
      </div>
    </li>
  )
}

function InvitationViewActivityLog(props: InvitationViewActivityLogProps) {
  const { logs, isRefreshing, onRefresh } = props

  if (!logs || logs.length === 0) return null

  return (
    <div className="invitation-view-activity-log px-4 py-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-medium">Activity Log</h3>
        <TooltipButton
          variant="ghost"
          size="tiny"
          iconSrc="refresh"
          isLoading={isRefreshing}
          aria-label="Refresh activity log"
          content="Refresh"
          onClick={onRefresh}
        />
      </div>
      <ol role="list" className="relative m-0 list-none p-0">
        {logs.map((log, index) => (
          <InvitationViewActivityLogRow
            key={log.Id}
            log={log}
            isLatest={index === 0}
            isFirst={index === 0}
            isLast={index === logs.length - 1}
          />
        ))}
      </ol>
    </div>
  )
}

export default InvitationViewActivityLog
